import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  Bell, CheckCheck, Trash2, Inbox, Filter, Loader2,
  Cake, CalendarCheck2, ListChecks, Clock, MessageSquare, Info,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/Loader';
import { notificationService } from '@/services';
import { fmtDateTime } from '@/utils/format';

const PAGE_SIZE = 20;

function iconFor(n) {
  if (n.meta?.kind === 'birthday') return Cake;
  switch (n.type) {
    case 'leave': return CalendarCheck2;
    case 'task': return ListChecks;
    case 'attendance': return Clock;
    case 'info':
      // Mentions / chats use type=info with a chat-style link
      if (n.link?.startsWith('/chats') || n.link?.includes('/chat')) return MessageSquare;
      return Info;
    default: return Bell;
  }
}

function colorFor(n) {
  if (n.meta?.kind === 'birthday') return 'from-rose-500 to-pink-500';
  switch (n.type) {
    case 'leave': return 'from-amber-500 to-orange-500';
    case 'task': return 'from-violet-500 to-purple-500';
    case 'attendance': return 'from-sky-500 to-cyan-500';
    case 'success': return 'from-emerald-500 to-teal-500';
    case 'warning': return 'from-amber-500 to-orange-500';
    default: return 'from-brand-500 to-indigo-500';
  }
}

export default function Notifications() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [filter, setFilter] = useState('all'); // 'all' | 'unread'

  const query = useInfiniteQuery({
    queryKey: ['notifications', 'all', filter],
    queryFn: ({ pageParam = 1 }) =>
      notificationService.list({ page: pageParam, limit: PAGE_SIZE, ...(filter === 'unread' ? { unread: true } : {}) }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, limit, total } = lastPage;
      return page * limit < total ? page + 1 : undefined;
    },
    refetchOnWindowFocus: false,
  });

  const items = useMemo(() => (query.data?.pages || []).flatMap((p) => p.items), [query.data]);
  const total = query.data?.pages?.[0]?.total ?? 0;
  const unreadCount = query.data?.pages?.[0]?.unreadCount ?? 0;

  // Infinite scroll trigger
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && query.hasNextPage && !query.isFetchingNextPage) {
          query.fetchNextPage();
        }
      },
      { rootMargin: '200px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  const onClickNotification = async (n) => {
    if (!n.read) {
      try {
        await notificationService.markRead(n._id);
        qc.invalidateQueries({ queryKey: ['notifications'] });
      } catch (e) { /* ignore */ }
    }
    if (n.link) navigate(n.link);
  };

  const markAllRead = async () => {
    try {
      await notificationService.markAllRead();
      qc.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('All notifications marked as read');
    } catch (e) {
      toast.error('Failed');
    }
  };

  const removeOne = async (e, id) => {
    e.stopPropagation();
    try {
      await notificationService.remove(id);
      qc.invalidateQueries({ queryKey: ['notifications'] });
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        subtitle={`${total} total · ${unreadCount} unread`}
        actions={
          <div className="flex items-center gap-2">
            <button onClick={markAllRead} className="btn-secondary" disabled={unreadCount === 0}>
              <CheckCheck size={14} /> Mark all read
            </button>
          </div>
        }
      />

      <Card className="!p-0 overflow-hidden">
        {/* Filter tabs */}
        <div className="flex items-center gap-1 p-2 border-b border-slate-200 dark:border-slate-800">
          <Filter size={14} className="text-slate-400 mx-2" />
          <FilterTab active={filter === 'all'} onClick={() => setFilter('all')}>
            All <span className="ml-1 text-xs text-slate-400">({total})</span>
          </FilterTab>
          <FilterTab active={filter === 'unread'} onClick={() => setFilter('unread')}>
            Unread <span className="ml-1 text-xs text-slate-400">({unreadCount})</span>
          </FilterTab>
        </div>

        {/* List */}
        {query.isLoading ? (
          <div className="py-12 grid place-items-center text-slate-500 text-sm">
            <Loader2 className="animate-spin" /> Loading notifications...
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={filter === 'unread' ? 'No unread notifications' : 'No notifications yet'}
            subtitle="When something happens involving you, it will show up here."
          />
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {items.map((n) => {
              const Icon = iconFor(n);
              const color = colorFor(n);
              return (
                <li key={n._id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => onClickNotification(n)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onClickNotification(n);
                      }
                    }}
                    className={`group w-full text-left px-4 sm:px-5 py-3.5 flex gap-3 items-start hover:bg-slate-50 dark:hover:bg-slate-800/40 transition relative cursor-pointer ${
                      !n.read ? 'bg-brand-50/30 dark:bg-brand-500/5' : ''
                    }`}
                  >
                    {!n.read && (
                      <span className="absolute left-1.5 top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-brand-500" />
                    )}
                    <div className={`shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br ${color} text-white grid place-items-center`}>
                      <Icon size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className={`font-medium text-sm ${!n.read ? 'text-slate-900 dark:text-slate-100' : 'text-slate-700 dark:text-slate-300'}`}>
                          {n.title}
                        </div>
                        <div className="text-[11px] text-slate-400 shrink-0 mt-0.5">{fmtDateTime(n.createdAt)}</div>
                      </div>
                      <div className="text-sm text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-2">{n.message}</div>
                      {n.link && (
                        <div className="text-[11px] text-brand-600 dark:text-brand-400 mt-1 font-medium">
                          Tap to open →
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={(e) => removeOne(e, n._id)}
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-slate-400 hover:text-rose-500 p-1.5 rounded"
                      title="Delete"
                      aria-label="Delete notification"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </li>
              );
            })}

            {/* Sentinel + footer states */}
            <li ref={sentinelRef} className="py-4 grid place-items-center text-xs text-slate-400">
              {query.isFetchingNextPage ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Loading more...
                </span>
              ) : !query.hasNextPage && items.length > 0 ? (
                <span className="inline-flex items-center gap-1">
                  <Inbox size={14} /> You've reached the end
                </span>
              ) : null}
            </li>
          </ul>
        )}
      </Card>
    </div>
  );
}

function FilterTab({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
        active
          ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300'
          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
      }`}
    >
      {children}
    </button>
  );
}
